import {
  CO_MAX_CARGO_STACK,
  CO_REBATE_BASE,
  CO_REBATE_CARGO_BONUS,
  CO_REBATE_PORTAL_URL,
  CO_REBATE_URL,
  CO_TAX_CREDIT,
  CO_TAX_CREDIT_URL,
} from "@/lib/constants";

const usd = (n: number) => "$" + n.toLocaleString("en-US");

/**
 * `/savings` — the merged money page.
 *
 * Five pages became one. This is the single largest structural change in the
 * redesign: a visitor who came to see what is in the shop had one door, and a
 * visitor who came to read about tax credits had five. `/financing`,
 * `/trade-in`, `/tax-voucher` and `/rebate` now 301 to the matching section
 * here, query string intact.
 *
 * EVERY DOLLAR FIGURE COMES FROM `@/lib/constants`, never from a literal typed
 * into this file. `script/check_co_facts.mjs` diffs those constants against the
 * two colorado.gov pages they cite and shouts when the state changes a number —
 * the credit was already cut from $450 to $225 once. A number hardcoded here
 * would be invisible to that check and would keep promising a discount the shop
 * cannot honor at the register.
 *
 * The two programs are constantly confused and must stay distinguishable: the
 * $225 is a tax credit ANY Colorado resident gets at the counter, no income
 * test; the $1,000+$300 is an income-qualified LOTTERY you register for during
 * a window. Naming the catch in the first clause is the house pattern.
 *
 * Rates and trade-in values say "ask in store" on purpose. Earlier drafts of
 * the standalone pages quoted "8.75% APR" and "$400–$1,200 in store credit";
 * both were removed as unverified and neither may come back without Ryan.
 */
export const savings = {
  hero: {
    line1: "Lower the upfront cost",
    line2: `by up to ${usd(CO_MAX_CARGO_STACK)}`,
    intro:
      "Compare Colorado's point-of-sale credit, the income-qualified rebate lottery, a trade-in, and financing. Each works differently; only the programs you qualify for reduce the price.",
  },

  sections: [
    {
      id: "tax-credit",
      eyebrow: "Every Colorado resident",
      heading: `Get ${usd(CO_TAX_CREDIT)} off a qualifying e-bike at purchase`,
      body: [
        `Any Colorado resident can use one credit per calendar year. There is no income test. Front Range Cargo Bikes is a participating retailer, so the discount comes off the eligible purchase at the shop.`,
        `Bring a Colorado driver's license or state ID.`,
      ],
      linkLabel: "See Colorado's official credit page",
      linkHref: CO_TAX_CREDIT_URL,
      figure: usd(CO_TAX_CREDIT),
      figureNote: "off, at the register",
    },
    {
      id: "rebate",
      eyebrow: "Income-qualified · a lottery",
      heading: `Apply for a separate rebate worth up to ${usd(CO_REBATE_BASE + CO_REBATE_CARGO_BONUS)}`,
      body: [
        `The rebate is ${usd(CO_REBATE_BASE)}, plus a ${usd(CO_REBATE_CARGO_BONUS)} cargo-bike bonus on a qualifying purchase. It is income-qualified and awarded by lottery after registration during a state window.`,
        `If you get it, it stacks with the ${usd(CO_TAX_CREDIT)} point-of-sale credit — ${usd(CO_MAX_CARGO_STACK)} in total. Use the state links below for its current registration windows and rules.`,
      ],
      linkLabel: "Register at the state's portal",
      linkHref: CO_REBATE_PORTAL_URL,
      secondaryLinkLabel: "How the rebate works",
      secondaryLinkHref: CO_REBATE_URL,
      figure: usd(CO_REBATE_BASE + CO_REBATE_CARGO_BONUS),
      figureNote: "if you're drawn",
    },
    {
      id: "financing",
      eyebrow: "Paying over time",
      heading: "Spread payments with an e‑bike loan",
      body: [
        "Clean Energy Credit Union offers fixed-rate e-bike loans. We point riders there because it is one of the few lenders with a loan built specifically for e-bikes; membership requires a savings account with a $5 minimum balance.",
        "We have no affiliation with CECU and earn nothing if you borrow there. Check their site for current rates and terms. They can change, so we don't hardcode them here.",
      ],
      linkLabel: "See CECU's e-bike loans",
      linkHref: "https://www.cleanenergycu.org/personal/electric-bicycle-loans/",
      figure: "fixed rate",
      figureNote: "check current terms",
    },
    {
      id: "trade-in",
      eyebrow: "Your old bike",
      heading: "Get an e-bike quote from Upway",
      body: [
        "Front Range Cargo Bikes partners with Upway for trade-ins. Upway evaluates eligible e-bikes and handles the quote and acceptance process online.",
        "If you accept its offer, Front Range Cargo Bikes can help you think through how to apply the value toward a replacement bike.",
      ],
      linkLabel: "Get an Upway quote",
      linkHref: "https://upway.co/pages/sell-your-ebike",
      figure: "online",
      figureNote: "eligibility and quote from Upway",
    },
  ],

  counterHeading: "Choose the bike before you finalize the math",
  counterBody:
    "Schedule a test ride to compare the bikes that fit your family. Once you have a likely choice, the shop can help separate confirmed discounts, estimated trade-in value, and financing.",
  counterCta: "Schedule a test ride",
} as const;
